import { useRef, useState, useEffect, useMemo } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { debounce } from 'lodash';
import { UseRefinementListProps, useCurrentRefinements, useRefinementList } from 'react-instantsearch';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import useMediaQuery from '@/frontastic/hooks/useMediaQuery';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import {
  getDealsFacetRefinementListOptions,
  getFacetItemDisplayLabel,
  isDealsFacetAttribute,
  prepareDealsFacetVisibleItems,
  shouldHideFacetCounts,
  shouldShowFacetSearchBar,
} from '@/helpers/utils/dealsFacetUtils';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { getSelectedDivision } from '@/helpers/utils/siteUtils';
import { desktop } from 'helpers/utils/screensizes';
import ContentStackImage from '../../../../common/contentStackImage/contentStackImage';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../ui/accordion';
import { getImagesByPath } from '../../details/helpers/utils/productDataUtils';

type CustomRefinementListProps = UseRefinementListProps & {
  facetEntries: [string, string][];
  isCollapsed: boolean;
  map: any;
  setMappedFacets: any;
  idx: any;
  nbHits: number;
  categoryURLFilters: any;
  categoryHit: any;
  setIsCategoryDataAvailable: any;
};

const debounceReload = debounce(() => {
  // window.location.reload();
}, 500);

const transformItems = (items: any, transformItemsAttribute: string, sizes: any[], widths: any[]) => {
  if (transformItemsAttribute === 'combined_sizes') {
    return items
      .map((item: any) => item.label.split(/-(.+)/)) // Split label into size and width
      .filter(([size, width]: any) => sizes.includes(size) || widths.includes(width)) // Check if size or width is in the list
      .map(([size, width]: any) => {
        if (size && width) return `${size}-${width}`; // If both exist, return "size-width"
        if (!size && width) return width; // If only width exists, return width
        if (size && !width) return size; // If only size exists, return size
        return null; // Fallback (shouldn't happen)
      })
      .filter(Boolean); // Remove any null values
  }
  return items;
};
export function CustomRefinementList({
  facetEntries,
  isCollapsed,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  map,
  setMappedFacets,
  idx,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  nbHits,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  categoryURLFilters,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  categoryHit,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setIsCategoryDataAvailable,
  ...props
}: CustomRefinementListProps) {
  const listLimit = props.attribute == 'combined_sizes' ? 999 : props.limit ?? 8;
  const listShowMoreLimit = listLimit >= 1000 ? listLimit + 1 : 1000;
  const isDealsFacet = isDealsFacetAttribute(props.attribute);
  const dealsFacetOrderRef = useRef<Map<string, number>>(new Map());
  const dealsFacetHideRef = useRef<string[]>([]);
  const dealsFacetRefinementListOptions = useMemo(
    () => (isDealsFacet ? getDealsFacetRefinementListOptions(dealsFacetOrderRef, dealsFacetHideRef) : {}),
    [isDealsFacet],
  );

  const { items, refine, searchForItems, canToggleShowMore, isShowingMore, toggleShowMore } = useRefinementList({
    ...props,
    limit: listLimit,
    showMoreLimit: listShowMoreLimit,
    ...dealsFacetRefinementListOptions,
  });
  const { items: itemFilters } = useRefinementList({
    attribute: 'combined_sizes',
    limit: 1000,
  });

  const [visibleItems, setVisibleItems] = useState<typeof items>([]);
  const [listHeight, setListHeight] = useState<string>('auto');
  const listRef = useRef<HTMLUListElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const initialItemsLengthRef = useRef<number | null>(null);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const projectSettings: ProjectSettings = useProjSettings();
  const displayName = facetEntries?.find(([key]) => key === props?.attribute)?.[1] || props?.attribute;
  const [searchTerm, setSearchTerm] = useState('');
  const blockFacet = projectSettings?.blockFacet || '';
  const blockFacetArray: any = blockFacet?.split(',').map((item) => item.trim());
  const facetDisplayNames = Object.values(facetEntries);
  const [isDesktopSize] = useMediaQuery(desktop);
  const { refine: clearRefine, items: currentFacet } = useCurrentRefinements();
  const params = useSearchParams();
  const facets = params.get('facet');
  const [value, setValue] = useState(['']);
  const [showFacet, setShowFacet] = useState(true);
  const [imageUrl, setImageUrl] = useState<any>([]);

  useEffect(() => {
    if (initialItemsLengthRef.current === null && items?.length > 0) {
      initialItemsLengthRef.current = items?.length;
    }

    if (isShowingMore) {
      const orderedItems = isDealsFacet
        ? prepareDealsFacetVisibleItems(items, dealsFacetOrderRef.current, searchTerm, dealsFacetHideRef.current)
        : items;
      setVisibleItems(orderedItems);
      if (items.length > 16 && listRef.current && displayName != 'Color') {
        setVisibleItems(orderedItems);
        setListHeight('auto'); // Ensure the height is auto when showing all items
      } else {
        setListHeight('auto');
      }
    } else {
      const orderedItems = isDealsFacet
        ? prepareDealsFacetVisibleItems(items, dealsFacetOrderRef.current, searchTerm, dealsFacetHideRef.current)
        : items;
      setVisibleItems(
        displayName === 'Color'
          ? orderedItems?.slice(0, 16)
          : isDealsFacet
          ? orderedItems
          : filteredValueArray.includes(displayName)
          ? orderedItems?.slice(0, 12)
          : orderedItems?.slice(0, 8),
      );
      setListHeight('auto');
    }

    const filterSizes =
      currentFacet
        .find((item: any) => item.attribute === 'refinementSize')
        ?.refinements.map((item: any) => item.value) || [];
    const filterWidths =
      currentFacet
        .find((item: any) => item.attribute === 'footwearWidth')
        ?.refinements.map((item: any) => item.value.replace(/ /g, '-')) || [];

    const avaiableWidthsSizes = transformItems(
      itemFilters,
      'combined_sizes',
      filterSizes ? filterSizes : [],
      filterWidths ? filterWidths : [],
    );
    if (avaiableWidthsSizes && avaiableWidthsSizes.length > 0) {
      if (
        props.attribute == 'footwearWidth' &&
        filterSizes &&
        filterSizes?.length > 0 &&
        filterWidths &&
        filterWidths?.length == 0
      ) {
        const width = Array.from(new Set(avaiableWidthsSizes.map((item: any) => item.split(/-(.+)/)[1])));
        setVisibleItems(
          (prev) => prev.filter((item: any) => width.includes(item.value.replace(/ /g, '-'))).sort(), // Only keep matching items
        );
      }
      if (
        props.attribute == 'refinementSize' &&
        filterSizes &&
        filterSizes?.length == 0 &&
        filterWidths &&
        filterWidths?.length > 0
      ) {
        const size = Array.from(new Set(avaiableWidthsSizes.map((item: any) => item.split(/-(.+)/)[0])));
        setVisibleItems(
          (prev) => prev.filter((item: any) => size.includes(item.value)).sort(), // Only keep matching items
        );
      }

      if (
        (props.attribute == 'footwearWidth' || props.attribute == 'refinementSize') &&
        filterSizes &&
        filterSizes?.length > 0 &&
        filterWidths &&
        filterWidths?.length > 0
      ) {
        if (props.attribute == 'footwearWidth') {
          const width = Array.from(new Set(avaiableWidthsSizes.map((item: any) => item?.split(/-(.+)/)[1])));
          setVisibleItems((prev) => prev.filter((item: any) => width.includes(item?.value?.replace(/ /g, '-'))).sort());
        }

        if (props.attribute == 'refinementSize') {
          const size = Array.from(new Set(avaiableWidthsSizes.map((item: any) => item.split(/-(.+)/)[0])));
          setVisibleItems((prev) => prev.filter((item: any) => size.includes(item.value)).sort());
        }
      }
    }
  }, [items, isShowingMore, searchTerm, isDealsFacet]);

  const shouldShowSearchBar = shouldShowFacetSearchBar(props?.attribute, initialItemsLengthRef.current);
  const hideFacetCounts = shouldHideFacetCounts(props?.attribute);
  const getItemDisplayLabel = (label: string) => getFacetItemDisplayLabel(props?.attribute, label);

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    setIsSearchActive(true);
    setSearchTerm(value);
    if (!isDealsFacet) {
      searchForItems(value);
    }
  };

  const backtoTop = () => {
    const plpParent = document.querySelectorAll('.productListSection')[0];
    if (plpParent instanceof HTMLElement) {
      if (isDesktopSize) {
        window.scrollTo({ top: plpParent.offsetTop, behavior: 'smooth' });
      }
      sessionStorage?.setItem('filtersSelectedPLP', 'true');
      setTimeout(() => {
        sessionStorage?.removeItem('filtersSelectedPLP');
      }, 1200);
    }
  };

  function removeRefinement(data: any, attributeToRemove: any, valueToRemove: any) {
    return data
      .map((item: any) => {
        if (item.attribute === attributeToRemove) {
          item.refinements = item.refinements.filter((refinement: any) => refinement.value !== valueToRemove);
        }
        return item;
      })
      .filter((item: any) => item.refinements.length > 0); // Remove attribute if refinements are empty
  }
  const handleRefinement = (checked: boolean, value: any) => {
    if (checked) {
      const filteredRefinements = removeRefinement(currentFacet, props?.attribute, value);
      if (typeof window !== 'undefined') {
        if (filteredRefinements && filteredRefinements.length == 0) {
          sessionStorage.removeItem('pageLoadFacets');
          const url = new URL(window.location.href.split('/_/')[0]);
          url.searchParams.delete('facet');
          router.replace(url?.toString(), {});
          window.history.replaceState({}, '', url?.toString());
          debounceReload();
        } else {
          backtoTop();
        }
      }
    } else {
      backtoTop();
    }
  };

  const handleRefine = (value: string, checked: boolean) => {
    if (props?.attribute === 'clearance') {
      if (facets) {
        const flters = facets.split('|');
        const clearanceEntry = flters.find((entry) => entry.startsWith('clearance:'));
        if (clearanceEntry) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const [_, value] = clearanceEntry.split(':');
          if (value === 'false') {
            clearRefine({
              attribute: props?.attribute,
              type: 'disjunctive',
              value: 'false',
              label: 'false',
            });
          }
        }
      }
      setTimeout(() => {
        refine(value);
      }, 1000);
      backtoTop();
    } else {
      refine(value);
      handleRefinement(checked, value);
    }
    setSearchTerm('');

    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
    setIsSearchActive(false);
  };

  const highlightMatchedText = (text: string) => {
    if (!searchTerm) {
      return <span className="font-normal">{text}</span>;
    }
    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return (
      <span>
        {parts.map((part, index) => (
          <span key={index} className={part.toLowerCase() === searchTerm.toLowerCase() ? 'font-bold' : 'font-normal'}>
            {part}
          </span>
        ))}
      </span>
    );
  };

  const filteredValueArray: any = [];

  facetDisplayNames
    .filter((arrayItem: any) => {
      return blockFacetArray.includes(arrayItem[0]);
    })
    .forEach((item) => {
      if (!filteredValueArray.includes(item[1])) {
        filteredValueArray.push(item[1]);
      }
    });

  useEffect(() => {
    if (isCollapsed) {
      setValue(['item-1']);
    } else {
      setValue(['']);
    }
  }, [isCollapsed]);

  const deleteMappedFacet = (attribute: string) => {
    setMappedFacets((prevMap: any) => {
      const updatedMap = new Map(prevMap);
      if (updatedMap.has(attribute)) {
        updatedMap.delete(attribute);
      }
      return updatedMap;
    });
  };

  const addMappedFacet = (attribute: string) => {
    setMappedFacets((prevMap: any) => {
      const updatedMap = new Map(prevMap);
      if (!updatedMap.has(attribute)) {
        updatedMap.set(attribute, idx);
      }
      return updatedMap;
    });
  };

  useEffect(() => {
    if (items?.length > 0) {
      addMappedFacet(props?.attribute);
    } else {
      deleteMappedFacet(props?.attribute);
    }
  }, [items]);

  useEffect(() => {
    if (items?.length === 1 && nbHits <= items?.[0]?.count && !items?.[0]?.isRefined && !isSearchActive) {
      setShowFacet(false);
      deleteMappedFacet(props?.attribute);
    } else {
      setShowFacet(true);
    }
  }, [items, nbHits, isSearchActive]);

  useEffect(() => {
    const currentSiteId = getSelectedDivision() ?? 'ZB';
    if (displayName === 'Color') {
      const urlArray = items
        .map((item) => {
          return `color/${item?.label?.toLowerCase()}.jpg`;
        })
        .join(',');
      if (urlArray !== '') {
        getImagesByPath(urlArray, currentSiteId).then((data: any) => {
          const safeData = Array.isArray(data) ? data : [];
          const filteredData = safeData?.filter(
            (item: any) => imageUrl?.findIndex((value: any) => value.title === item.title) === -1,
          );
          setImageUrl([...imageUrl, ...filteredData]);
        });
      }
    }
  }, [items]);

  const handleAccordion = (accordionValue: string[]) => {
    setValue(accordionValue);
    if (accordionValue?.length === 0) {
      setSearchTerm('');
      if (!isDealsFacet) {
        searchForItems('');
      }
      setIsSearchActive(false);
    }
  };

  return (
    <>
      <Accordion
        type="multiple"
        defaultValue={isCollapsed ? ['item-1'] : []}
        value={value}
        onValueChange={handleAccordion}
        className="w-full lg:px-3"
      >
        {(items?.length > 0 || isSearchActive) && showFacet ? (
          <>
            <p id={displayName.replace(/\s+/g, '')} className="sr-only">
              radix-:ria
            </p>

            <AccordionItem value="item-1" className="-mx-4 px-4 lg:mx-0 lg:px-0">
              {displayName ? (
                <AccordionTrigger className="-mx-4 flex items-center justify-between border-b border-silverGray px-4 text-left text-base font-bold capitalize leading-[21px] text-black no-underline hover:no-underline md:text-15 lg:mx-0 lg:px-0 ">
                  {displayName}
                </AccordionTrigger>
              ) : (
                <></>
              )}

              <AccordionContent
                aria-labelledby={displayName.replace(/\s+/g, '')}
                id={`trigger-${displayName.replace(/\s+/g, '')}`}
                className={`pt-4`}
              >
                <span className="sr-only" id={displayName.replace(/\s+/g, '')}></span>
                {shouldShowSearchBar && displayName.trim() !== 'Price' ? (
                  <div>
                    <label htmlFor={`searchInput_${displayName}`} className="sr-only">
                      Search {displayName.toLocaleLowerCase()}
                    </label>
                    <input
                      id={`searchInput_${displayName}`}
                      ref={searchInputRef}
                      className="gbunmask searchCrossBlack mx-auto mb-4 ml-px w-[calc(100%_-_4px)] rounded-md border border-silverGray px-4 py-2"
                      type="search"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      maxLength={512}
                      onChange={handleSearchInputChange}
                      placeholder={`Search ${displayName.toLocaleLowerCase()}`}
                    />
                  </div>
                ) : (
                  <></>
                )}
                {displayName === 'Color' ? (
                  <ul
                    ref={listRef}
                    className={`flex flex-wrap gap-3 ${
                      listHeight !== 'auto' ? 'custom-scrollbar overflow-y-scroll' : ''
                    }`}
                  >
                    {visibleItems?.map((item, index) => {
                      const imgSrc =
                        imageUrl?.filter((image: any) => {
                          const imageTitle = image?.title?.split('?')[0];
                          return imageTitle === `${item?.label.toLowerCase()}.jpg`;
                        })?.[0]?.url ?? '';
                      return (
                        <li key={`${index}_${item?.label}`} className="shrink-0">
                          <button
                            type="button"
                            title={`${item?.label}`}
                            aria-label={item?.label}
                            onClick={() => handleRefine(item?.value, item?.isRefined)}
                            className={`mx-1 my-1.5 rounded-full outline outline-offset-2 ${
                              item?.isRefined ? 'outline-[3px] outline-black' : 'outline-2 outline-silverGray'
                            } focus:outline-3 size-8 focus:outline-black`}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleRefine(item?.value, item?.isRefined);
                              }
                            }}
                          >
                            <span className="sr-only">{item?.label + ' color swatch'}</span>
                            <ContentStackImage
                              className="size-7 lg:size-8 rounded-full"
                              src={imgSrc}
                              imageWidth={32}
                              imageHeight={32}
                              alt={item?.label + ' color swatch'}
                              fullWidth={true}
                            />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : filteredValueArray?.includes(displayName) ? (
                  <ul
                    ref={listRef}
                    className={`flex flex-wrap gap-[7px] ${listHeight !== 'auto' ? 'gap-[5px]' : ''}`}
                    style={{ height: listHeight }}
                  >
                    {visibleItems?.map((item, index) => {
                      return (
                        <li key={`${index}_${item?.label}`} className="shrink-0">
                          <button
                            type="button"
                            aria-label={`Refine search by ${item?.label} (${item?.value})`}
                            onClick={() => {
                              handleRefine(item?.value, item?.isRefined);
                            }}
                            lang="en"
                            className={`
                                              min-h-10 w-24 hyphens-auto border-2 p-1 text-sm leading-4
                                              ${
                                                item?.isRefined
                                                  ? 'border-2 border-black font-bold'
                                                  : 'border-silverGray font-medium'
                                              }
                                              flex items-center justify-center overflow-hidden
                                            `}
                          >
                            {item?.value}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <ul
                    ref={listRef}
                    className={`grid gap-4 px-1 ${listHeight !== 'auto' ? 'custom-scrollbar overflow-y-scroll' : ''}`}
                    style={{ height: listHeight }}
                    role="list"
                  >
                    {visibleItems?.map((item, index) => (
                      <li key={`${index}_${item?.label}`} className="flex gap-3" role={`Listitem`}>
                        <section className="flex shrink-0 items-start pt-px">
                          <input
                            type="checkbox"
                            id={`${displayName.replace(/\s+/g, '-')}-${item?.label?.replace(/\s+/g, '-')}`}
                            aria-labelledby={`${displayName.replace(/\s+/g, '-')}-${item?.label?.replace(/\s+/g, '-')}`}
                            checked={item?.isRefined}
                            onChange={() => handleRefine(item?.value, item?.isRefined)}
                            className="custom-checkbox focus:shadow:none size-6 lg:size-4 mt-px shadow-none hover:cursor-pointer focus:outline-none focus:ring-0"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleRefine(item?.value, item?.isRefined);
                              }
                            }}
                          />
                        </section>

                        <label
                          htmlFor={`${displayName.replace(/\s+/g, '-')}-${item?.label?.replace(/\s+/g, '-')}`}
                          className="text-base leading-[19.4px] text-black hover:cursor-pointer lg:text-sm"
                          tabIndex={0} // Make label focusable too for keyboard navigation
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              // Handle Enter/Space for label to toggle checkbox
                              e.preventDefault();
                              handleRefine(item?.value, item?.isRefined); // Trigger the same action as onClick
                            }
                          }}
                        >
                          <span className="inline-block hover:underline">
                            {highlightMatchedText(getItemDisplayLabel(item?.label))}
                          </span>
                          {!hideFacetCounts && <span className="inline-block"> ({item?.count?.toLocaleString()})</span>}
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
                {canToggleShowMore && props?.attribute !== 'refinementSize' && props?.attribute !== 'footwearWidth' ? (
                  <button
                    onClick={toggleShowMore}
                    disabled={!canToggleShowMore}
                    className="mt-5 px-1 text-base leading-[19.4px] text-black underline lg:text-sm"
                    type="button"
                  >
                    {isShowingMore
                      ? `${mapLocaleToMeaningfulFormat().viewLess}`
                      : `${mapLocaleToMeaningfulFormat().viewMore}`}
                  </button>
                ) : (props?.attribute === 'refinementSize' || props?.attribute === 'footwearWidth') &&
                  visibleItems?.length >= 12 &&
                  canToggleShowMore ? (
                  <button
                    onClick={toggleShowMore}
                    disabled={!canToggleShowMore}
                    className="mt-5 px-1 text-base leading-[19.4px] text-black underline lg:text-sm"
                    type="button"
                  >
                    {isShowingMore
                      ? `${mapLocaleToMeaningfulFormat().viewLess}`
                      : `${mapLocaleToMeaningfulFormat().viewMore}`}
                  </button>
                ) : (
                  <></>
                )}
              </AccordionContent>
            </AccordionItem>
          </>
        ) : (
          <></>
        )}
      </Accordion>
    </>
  );
}
